import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useSession } from '@/components/auth/user-provider';
import { useCustomerSkin } from '@/components/tenancy/customer-brand-provider';
import { resolveLayoutManageTargets, type LayoutManageTargets } from '@/lib/managed-mode';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { PageErrorBoundary } from '@/components/layout/page-error-boundary';
import { LayoutEditorGuardContext, type LayoutEditorGuard } from './layout-editor-guard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const LayoutManagementContent = React.lazy(() => import('./layout-management-content'));

interface ManagedModeValue {
  available: boolean;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  targets: LayoutManageTargets;
  openEditor: (kind: 'home' | 'navigation') => void;
}

const none: LayoutManageTargets = { navigation: null, home: null };
const ManagedModeContext = React.createContext<ManagedModeValue>({
  available: false, enabled: false, setEnabled: () => {}, targets: none, openEditor: () => {}
});

export function useManagedMode(): ManagedModeValue {
  return React.useContext(ManagedModeContext);
}

/** UI preference only. The server checks every layout write independently. */
export function ManagedModeProvider({ children }: { children: React.ReactNode }) {
  const { session } = useSession();
  const { pathname } = useLocation();
  const { layoutAuthority } = useCustomerSkin();
  const user = session.status === 'signedIn' ? session.user : null;
  const targets = resolveLayoutManageTargets(user, layoutAuthority, pathname);
  const available = Boolean(targets.navigation || targets.home);
  const [requested, setRequested] = React.useState(false);
  const [editor, setEditor] = React.useState<{ kind: 'home' | 'navigation'; to: string; pathname: string } | null>(null);
  const target = editor ? targets[editor.kind] : null;
  const editorOpen = Boolean(requested && available && editor && target?.to === editor.to && pathname === editor.pathname);
  const trigger = React.useRef<HTMLElement | null>(null);
  const [guard, setGuard] = React.useState<LayoutEditorGuard>({ dirty: false, busy: false });
  const [discardOpen, setDiscardOpen] = React.useState(false);
  function closeEditor() {
    if (guard.busy) return;
    if (guard.dirty) setDiscardOpen(true);
    else setEditor(null);
  }

  React.useEffect(() => {
    if (!editorOpen) { setEditor(null); setDiscardOpen(false); }
  }, [editorOpen]);

  React.useEffect(() => {
    if (!available) setRequested(false);
  }, [available]);

  const value: ManagedModeValue = {
    available,
    enabled: available && requested,
    setEnabled: setRequested,
    targets,
    openEditor: (kind) => {
      if (!requested || !targets[kind]) return;
      trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setEditor({ kind, to: targets[kind].to, pathname });
    }
  };

  return <ManagedModeContext.Provider value={value}>
    {children}
    <LayoutEditorGuardContext.Provider value={setGuard}>
    <Sheet open={editorOpen} onOpenChange={(open) => { if (!open) closeEditor(); }}>
      <SheetContent side="right" className="flex w-full max-w-3xl flex-col overflow-hidden p-0" showCloseButton={!guard.busy}
        onEscapeKeyDown={event => { if (guard.busy || guard.dirty) { event.preventDefault(); closeEditor(); } }}
        onPointerDownOutside={event => { if (guard.busy || guard.dirty) { event.preventDefault(); closeEditor(); } }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const mobileNavigation = trigger.current?.closest('aside[aria-label="Navigation"]');
          const destination = mobileNavigation
            ? document.querySelector<HTMLButtonElement>('button[aria-label="Open sidebar"]')
            : trigger.current;
          if (destination?.isConnected) destination.focus();
        }}>
        <SheetHeader className="shrink-0 border-b p-6 pr-12">
          <SheetTitle>{editor?.kind === 'home' ? 'Manage Home' : 'Manage navigation'}</SheetTitle>
          <SheetDescription>Edit the layout, then save to apply your changes.</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {editorOpen && editor && target ? <PageErrorBoundary>
            <React.Suspense fallback={<p role="status">Loading layout…</p>}>
              <LayoutManagementContent key={editor.to} kind={editor.kind} target={target} />
            </React.Suspense>
          </PageErrorBoundary> : null}
        </div>
      </SheetContent>
    </Sheet>
    <Dialog open={discardOpen && editorOpen} onOpenChange={setDiscardOpen}>
      <DialogContent><DialogHeader><DialogTitle>Discard unpublished homepage changes?</DialogTitle><DialogDescription>Your draft will be lost. Published Home stays unchanged.</DialogDescription></DialogHeader>
        <DialogFooter><Button variant="outline" onClick={() => setDiscardOpen(false)}>Keep editing</Button><Button onClick={() => { setDiscardOpen(false); setEditor(null); }}>Discard and close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    </LayoutEditorGuardContext.Provider>
  </ManagedModeContext.Provider>;
}
